import { useState } from "react";
import type {
  Action,
  ActionDecision,
  CannedScenario,
  Decision,
} from "../data/types";

interface Props {
  scenario: CannedScenario;
  decisions: Map<string, ActionDecision>;
  setDecisions: (
    updater:
      | Map<string, ActionDecision>
      | ((prev: Map<string, ActionDecision>) => Map<string, ActionDecision>),
  ) => void;
  onSubmit: () => void;
}

export function ActionPlan({ scenario, decisions, setDecisions, onSubmit }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const setDecision = (actionId: string, decision: Decision) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      next.set(actionId, { decision });
      return next;
    });
  };

  const setAll = (decision: Decision) => {
    setDecisions(() => {
      const next = new Map<string, ActionDecision>();
      for (const a of scenario.action_plan.actions) {
        next.set(a.action_id, { decision });
      }
      return next;
    });
  };

  const approvedCount = countDecisions(decisions, "approve");
  const rejectedCount = countDecisions(decisions, "reject");
  const escalatedCount = countDecisions(decisions, "escalate");
  const totalActions = scenario.action_plan.actions.length;
  const decided = approvedCount + rejectedCount + escalatedCount;
  const allDecided = decided === totalActions;

  return (
    <div>
      {/* Playbook-source banner — explicit about where these recommendations came from */}
      <div className="playbook-banner card">
        <div className="playbook-banner-icon" aria-hidden="true">
          <PlaybookIcon />
        </div>
        <div className="playbook-banner-body">
          <div className="playbook-banner-eyebrow">RECOMMENDED ACTIONS · sourced from playbook{scenario.action_plan.matched_playbooks.length > 1 ? "s" : ""}</div>
          <div className="playbook-banner-title">
            Response Agent matched{" "}
            <strong>{scenario.action_plan.matched_playbooks.length}</strong>{" "}
            playbook{scenario.action_plan.matched_playbooks.length > 1 ? "s" : ""} for
            this incident type and extracted{" "}
            <strong>{totalActions}</strong> action
            {totalActions !== 1 ? "s" : ""}.
          </div>
          <div className="playbook-list">
            {scenario.action_plan.matched_playbooks.map((p) => (
              <span key={p} className="playbook-chip mono">
                <PlaybookFileIcon /> {p}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="action-plan-layout">
        <aside className="action-plan-sidebar">
          <h3>Plan summary</h3>
          <div className="summary">{scenario.action_plan.summary}</div>

          <h3 className="mt-4">Stats</h3>
          <div className="stats">
            <div className="stat-row">
              <span className="label">Total actions</span>
              <span>{totalActions}</span>
            </div>
            <div className="stat-row">
              <span className="label">Matched playbooks</span>
              <span>{scenario.action_plan.matched_playbooks.length}</span>
            </div>
            <div className="stat-row">
              <span className="label">Reversible</span>
              <span>
                {scenario.action_plan.actions.filter((a) => a.reversible).length}/{totalActions}
              </span>
            </div>
            <div className="stat-row">
              <span className="label">High-risk</span>
              <span>
                {scenario.action_plan.actions.filter((a) => a.risk_level === "high").length}
              </span>
            </div>
          </div>
        </aside>

        <div className="action-plan-list">
          {scenario.action_plan.actions.map((action) => {
            const decision = decisions.get(action.action_id);
            const isExpanded = expanded === action.action_id;
            const decClass = decision
              ? decision.decision === "approve"
                ? "approved"
                : decision.decision === "reject"
                  ? "rejected"
                  : "escalated"
              : "";

            return (
              <div
                key={action.action_id}
                className={`action-row ${decClass} ${isExpanded ? "expanded" : ""}`}
              >
                <button
                  type="button"
                  className="action-row-summary"
                  onClick={() => setExpanded(isExpanded ? null : action.action_id)}
                  aria-expanded={isExpanded}
                >
                  <span className="mono action-id">{action.action_id}</span>
                  <div className="action-name-block">
                    <div className="action-name">{action.action_type}</div>
                    <div className="target">→ {action.target}</div>
                    <div className="playbook-source mono">
                      <PlaybookFileIcon /> {action.playbook_source}
                    </div>
                  </div>
                  <span className={`risk-chip ${action.risk_level}`}>
                    <span className={`dot ${action.risk_level}`} />
                    {action.risk_level} risk
                  </span>
                  <span
                    className={`reversibility ${action.reversible ? "reversible" : "one-way"}`}
                  >
                    {action.reversible ? "◇ reversible" : "✗ one-way"}
                  </span>
                  <span className="arrow">{isExpanded ? "▾" : "▸"}</span>
                </button>

                {isExpanded && (
                  <ActionDetail
                    action={action}
                    decision={decision}
                    setDecision={setDecision}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="action-plan-actions">
        <div className="decision-summary">
          <strong>
            {decided}/{totalActions}
          </strong>{" "}
          decided ·{" "}
          <span style={{ color: "var(--risk-low)" }}>{approvedCount} approve</span> ·{" "}
          <span style={{ color: "var(--risk-high)" }}>{rejectedCount} reject</span> ·{" "}
          <span style={{ color: "var(--cf-purple)" }}>{escalatedCount} escalate</span>
        </div>
        <div className="button-group">
          <button type="button" className="button ghost" onClick={() => setAll("reject")}>
            Reject all
          </button>
          <button type="button" className="button" onClick={() => setAll("approve")}>
            Approve all
          </button>
          <button
            type="button"
            className="button primary"
            onClick={onSubmit}
            disabled={!allDecided}
          >
            Submit decisions →
          </button>
        </div>
      </div>
    </div>
  );
}

function countDecisions(decisions: Map<string, ActionDecision>, target: Decision): number {
  let n = 0;
  for (const d of decisions.values()) if (d.decision === target) n++;
  return n;
}

function ActionDetail({
  action,
  decision,
  setDecision,
}: {
  action: Action;
  decision: ActionDecision | undefined;
  setDecision: (id: string, d: Decision) => void;
}) {
  return (
    <div className="action-detail">
      <div className="meta-grid">
        <div>
          <div className="section-label">Domain</div>
          <div>{action.domain}</div>
        </div>
        <div>
          <div className="section-label">Integration</div>
          <div>{action.integration}</div>
        </div>
        <div>
          <div className="section-label">Prerequisites</div>
          <div className="mono text-sm">
            {action.prerequisites.length > 0
              ? action.prerequisites.join(", ")
              : "none"}
          </div>
        </div>
      </div>

      <div className="section-label">Rationale (from response agent · sourced from playbook)</div>
      <p>{action.rationale}</p>

      <div className="decision-row">
        <button
          type="button"
          className={`button ${decision?.decision === "approve" ? "success" : ""}`}
          onClick={() => setDecision(action.action_id, "approve")}
        >
          ✓ Approve
        </button>
        <button
          type="button"
          className={`button ${decision?.decision === "reject" ? "danger" : ""}`}
          onClick={() => setDecision(action.action_id, "reject")}
        >
          ✗ Reject
        </button>
        <button
          type="button"
          className={`button ${decision?.decision === "escalate" ? "primary" : ""}`}
          onClick={() => setDecision(action.action_id, "escalate")}
        >
          ⚠ Escalate
        </button>
      </div>
    </div>
  );
}

function PlaybookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function PlaybookFileIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-1px", marginRight: 4 }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}
